/// <reference path='../../../typings.d.ts' />
import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { AuthenticationService } from "../../shared/authentication.service";
import { environment } from "../../../environments/environment";
import { AccountService } from "../../account/account.service";
import {
  AuthenticationResponse,
  AuthenticationErrorResponse,
  InvalidLoginCause,
} from "../../../models/account/account.model";
import {
  UserShortcut,
  Module,
  ShortcutCategorie,
} from "../../../models/shortcut/shortcut.model";
import { DialogConfirmComponent } from "../../shared/components/dialog-confirm/dialog-confirm.component";
import { MatDialog } from "@angular/material/dialog";
import { UserHelperService } from "../../shared/userHelper.service";
import { ManagementService } from "../../management/management.service";
import { ShortcutService } from "../../shared/shortcut.service";
import { VersionService } from "../../shared/version.service";

@Component({
  selector: "login-page",
  templateUrl: "./login-page.component.html",
  styleUrls: ["./login-page.component.scss"],
})
export class LoginPageComponent implements OnInit {
  public formGroup: UntypedFormGroup;
  public remainingAttempts: number;
  public showExpireWarning = false;
  public daysUntilExpiration: number | undefined;
  public disableLogin = false;
  public badEmail = false;
  public showPassword = false;
  public isAdmin = false;
  public appVersion: string;
  private key = "session";

  constructor(
    protected formBuilder: UntypedFormBuilder,
    private auth: AuthenticationService,
    private router: Router,
    private account: AccountService,
    public dialog: MatDialog,
    private userHelperService: UserHelperService,
    private userService: ManagementService,
    private keyboardShortcutService: ShortcutService,
    private route: ActivatedRoute,
    version: VersionService
  ) {
    this.appVersion = version.data.packageVersion || "";
  }

  ngOnInit(): void {
    this.isAdmin = false;
    this.userHelperService.setUserType(this.isAdmin);
    this.formGroup = this.formBuilder.group({
      email: ["", [Validators.email, Validators.required]],
      password: ["", Validators.required],
    });
    if (localStorage.getItem(this.key)) this.auth.logout();
  }

  login(): void {
    if (!this.formGroup.valid || this.disableLogin) return;
    this.disableLogin = true;
    const email = this.formGroup.get("email").value;
    const password = this.formGroup.get("password").value;
    this.account.signIn(email, password).subscribe(
      (answer: AuthenticationResponse) => {
        this.daysUntilExpiration = answer.days_until_password_expiration;
        if (!answer.access_token) return;
        this.userHelperService.setUserName(answer.username);
        this.userHelperService.setEmotionTransferActivated(
          answer.emotionTransfer
        );
        this.auth.storeAuthenticationResponse(answer);
        this.userService.getCurrentUserType().subscribe((data) => {
          this.isAdmin = !!data;
          this.userHelperService.setUserType(this.isAdmin);
        });
        if (
          this.daysUntilExpiration <
          environment.account.daysBeforeExpirationForWarning
        ) {
          this.auth.validateSessionOrRedirect().subscribe(() => {
            this.showExpireWarning = true;
          });
        } else {
          this.keyboardShortcutService
            .getAllActionsByUser()
            .subscribe((actions: UserShortcut[]) => {
              const userShortcuts = actions.reduce((acc: any, cur: any) => {
                const category = cur.shortcutAction.categorie;
                const module = cur.shortcutAction.module;
                const target =
                  category === ShortcutCategorie.VideoPlayer
                    ? "videoPlayer"
                    : module === Module.Writing || module === Module.Any
                    ? "Writing"
                    : module === Module.Recording
                    ? "Recording"
                    : null;
                if (target) {
                  if (!acc[target]) acc[target] = {};
                  acc[target][cur.shortcutKeys] = cur.shortcutAction.action;
                }
                return acc;
              }, {});
              localStorage.setItem(
                "userShortcuts",
                JSON.stringify(userShortcuts)
              );
            });
          this.userHelperService.setTenantId(answer.tenantId);
          this.router.navigateByUrl(
            this.route.snapshot.queryParams["backUrl"] || "/projects"
          );
        }
      },
      (error) => {
        const authError: AuthenticationErrorResponse = error.error || {};
        this.badEmail = false;
        this.disableLogin = false;
        if (authError.remaining_attempts)
          this.remainingAttempts = authError.remaining_attempts;
        else if (authError.remaining_attempts === 0)
          this.processBlockedAccount();
        else if (authError.days_until_password_expiration === 0)
          this.router.navigateByUrl("/account/expiredPassword");
        else if (authError.error === InvalidLoginCause.InvalidGrant)
          this.badEmail = true;
      }
    );
  }

  private processBlockedAccount(): void {
    const dialogRef = this.dialog.open(DialogConfirmComponent, {
      width: "600px",
      height: "260px",
    });
    dialogRef.componentInstance.data = {
      message: "signIn.dialogBoxLocked.message",
      title: "signIn.dialogBoxLocked.title",
      confirmLabel: "signIn.dialogBoxLocked.confirm",
    };
  }
}
